import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './App.css';
import Home from './pages/Home';
import { Toaster } from './components/ui/sonner';
import SEOHead from './components/SEOHead';
import ErrorBoundary from './components/ErrorBoundary';
import axios from 'axios';

import { PrivacyPolicy, TermsOfService, CookiePolicy } from './pages/LegalPages';
import SectionPage from './pages/SectionPage';
import LandingPage from './pages/LandingPage';
import EventDetailPage from './pages/EventDetailPage';

const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// GTM noscript tag — Google guidelines require this immediately after <body>
function GTMNoscript({ gtmId }) {
  if (!gtmId) return null;
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
        title="GTM noscript"
      />
    </noscript>
  );
}

function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/content/pages/${slug}`).then(r => r.data).catch(() => null),
      axios.get(`${API_URL}/content/settings`).then(r => r.data).catch(() => ({})),
    ]).then(([pageData, settingsData]) => {
      setPage(pageData);
      setSettings(settingsData);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0F1419] flex items-center justify-center"><div className="text-[#C9A961]">Loading...</div></div>;
  if (!page) return <div className="min-h-screen bg-[#0F1419] flex items-center justify-center"><div className="text-[#E8E4DC]">Page not found</div></div>;

  return (
    <div className="min-h-screen bg-[#0F1419]">
      <SEOHead
        title={`${page.title} | Wentworth House Hotel`}
        description={page.seo_description || page.title}
        keywords={page.seo_keywords || ''}
        path={`/page/${slug}`}
        settings={settings}
      />
      <header className="bg-[#0F1419] border-b border-[#C9A961]/20 py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="text-[#C9A961] font-serif text-xl">Wentworth House</a>
          <a href="/" className="text-sm text-[#C9A961]/60 hover:text-[#C9A961]">&larr; Back to Home</a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-serif text-[#E8E4DC] mb-8">{page.title}</h1>
        <div className="prose prose-invert max-w-none text-[#E8E4DC]/80" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content || '', {
          ALLOWED_TAGS: ['a', 'strong', 'em', 'br', 'li', 'ul', 'ol', 'p', 'span', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'code'],
          ALLOWED_ATTR: ['href', 'class', 'target', 'rel'],
        }) }} />
      </main>
    </div>
  );
}

const LoadingFallback = () => (
  <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
    <div className="text-[#C9A961] text-lg font-serif">Loading...</div>
  </div>
);

function AppRouter() {
  const [gtmId, setGtmId] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/content/settings`).then(r => {
      if (r.data.google_tag_manager) setGtmId(r.data.google_tag_manager);
    }).catch(() => {});
  }, []);

  return (
    <>
      <GTMNoscript gtmId={gtmId} />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<SectionPage sectionKey="about" fallbackTitle="About Us" />} />
          <Route path="/rooms" element={<SectionPage sectionKey="rooms" fallbackTitle="Our Rooms" />} />
          <Route path="/gallery" element={<SectionPage sectionKey="gallery" fallbackTitle="Photo Gallery" />} />
          <Route path="/offers" element={<SectionPage sectionKey="offers" fallbackTitle="Special Offers" />} />
          <Route path="/events" element={<SectionPage sectionKey="events" fallbackTitle="Events in Bath" />} />
          <Route path="/contact" element={<SectionPage sectionKey="contact" fallbackTitle="Contact Us" />} />
          <Route path="/discover-bath" element={<SectionPage sectionKey="discover_bath" fallbackTitle="Discover Bath" />} />
          <Route path="/amenities" element={<SectionPage sectionKey="amenities" fallbackTitle="Hotel Amenities" />} />
          <Route path="/book-direct" element={<SectionPage sectionKey="book_direct" fallbackTitle="Book Direct & Save" />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/page/:slug" element={<DynamicPage />} />
          <Route path="/lp/:slug" element={<LandingPage />} />
          <Route path="/events/:slug" element={<EventDetailPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster />
    </div>
  );
}

export default App;
